import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu"
import { useQuery } from "@rocicorp/zero/react"
import { useLocation, useNavigate } from "@tanstack/react-router"
import {
	IconCopyFill18,
	IconPenFill18,
	IconTrashFill18,
} from "nucleo-ui-fill-18"
import { IconPlusOutline18 as PlusIcon } from "nucleo-ui-outline-18"
import { useMedia } from "react-use"

import type { CustomView, User } from "@productlane/zero/schema"
import { cn } from "@productlane/lib/src/cn"
import { soleAssigneeId } from "@productlane/lib/src/custom-view-assignee"
import { Avatar, Badge, IconButton, NucleoFillIcon } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisIcon as MoreHorizontal } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import {
	NavSectionBody,
	NavSectionHeader,
} from "@/components/shared/nav-section-header"
import { SidebarAddButton } from "@/components/shared/sidebar-add-button"
import { toast } from "@/components/toast"
import { generateId } from "@/lib/id"
import { pressToActivate } from "@/lib/press-to-navigate"
import { useThreadState } from "@/lib/states/global-state"
import {
	INBOX_SECTIONS,
	useInboxNavStore,
	useIsInboxSectionOpen,
} from "@/lib/states/inbox-nav-state"
import { useZero } from "@/lib/zero"
import { useThreadsFilterState } from "../../threads/filter/threads-filter-state"
import { viewOwnsStatusFilter } from "../helpers/assigned-sub-tab"
import { getFormattedThreadCount } from "../useThreadCount"
import { EditViewDialog } from "./edit-view-dialog"
import {
	INBOX_NAV_ADD_BUTTON,
	INBOX_NAV_COUNT,
	INBOX_NAV_GROUP,
	INBOX_NAV_ICON,
	INBOX_NAV_LEAD,
	INBOX_NAV_ROW,
	INBOX_NAV_ROW_ACTIVE,
	INBOX_NAV_ROW_INACTIVE,
	INBOX_NAV_ROW_MOBILE,
	INBOX_NAV_SECTION_HEADER,
	VIEW_ICON_CLASS,
} from "./inbox-nav-styles"

const DEFAULT_ICON = "Layers2"

// Each badge syncs up to cap + 1 threads to count them, so the cost grows with
// every view. The first views match the inbox tabs; the rest stay cheap.
const FULL_COUNT_VIEWS = 10
const FULL_COUNT_CAP = 99
const REDUCED_COUNT_CAP = 20

export const customViewCountCap = (index: number) =>
	index < FULL_COUNT_VIEWS ? FULL_COUNT_CAP : REDUCED_COUNT_CAP

function useCustomViewThreadCount(view: CustomView, cap: number) {
	const [threads] = useQuery(
		queries.threadCount({
			tab: "custom-view",
			// A view with its own status filter counts what that filter asks for;
			// without one the badge matches the Open sub-tab the row opens on.
			stateScope: viewOwnsStatusFilter(view.filters) ? undefined : "open",
			filters: view.filters,
			limit: cap + 1,
		}),
	)
	const count = threads.length
	return { count, label: getFormattedThreadCount(count, cap) }
}

const contextMenuItemClass =
	"relative flex cursor-default select-none items-center rounded-md p-1 px-2 py-1.5 text-sm font-medium outline-none focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-gray-800"

function CustomViewItem({
	view,
	countCap,
	isActive,
	assignee,
	onSelect,
	onEdit,
	onDelete,
	onCopyLink,
}: {
	view: CustomView
	countCap: number
	isActive: boolean
	assignee?: User
	onSelect: () => void
	onEdit: (view: CustomView) => void
	onDelete: (id: string) => void
	onCopyLink: (view: CustomView) => void
}) {
	const { count, label } = useCustomViewThreadCount(view, countCap)

	return (
		<ContextMenuPrimitive.Root>
			<ContextMenuPrimitive.Trigger asChild>
				<div
					role="button"
					tabIndex={0}
					{...pressToActivate(onSelect)}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault()
							onSelect()
						}
					}}
					className={cn(
						INBOX_NAV_ROW,
						INBOX_NAV_ROW_MOBILE,
						"w-full cursor-default outline-none max-sm:w-auto",
						isActive ? INBOX_NAV_ROW_ACTIVE : INBOX_NAV_ROW_INACTIVE,
					)}
				>
					<div className={INBOX_NAV_LEAD}>
						<div
							className={cn(
								INBOX_NAV_ICON,
								"group/icon relative flex transform-gpu items-center justify-center",
							)}
						>
							{assignee ? (
								<Avatar
									image={assignee.imageUrl}
									name={assignee.name ?? assignee.email ?? ""}
									size="xxs"
									roundness="full"
									className="h-3.5 min-h-3.5 w-3.5 min-w-3.5 text-[0.5rem] group-hover/icon:opacity-0"
								/>
							) : (
								<NucleoFillIcon
									icon={view.icon ?? DEFAULT_ICON}
									className={cn(VIEW_ICON_CLASS, "group-hover/icon:opacity-0")}
								/>
							)}
							<div className="absolute inset-0 flex items-center justify-center truncate opacity-0 group-hover/icon:opacity-100">
								<DropdownMenu
									className="w-[185px]"
									contentProps={{ align: "start", alignOffset: -9 }}
									trigger={
										<IconButton
											Icon={MoreHorizontal}
											tabIndex={0}
											variant="ghost"
											onClick={(e) => e.stopPropagation()}
											// The row navigates on mousedown, so the menu button stops it there too.
											onMouseDown={(e) => e.stopPropagation()}
											className="flex h-3 w-3 items-center justify-center rounded text-gray-400 hover:bg-transparent hover:text-gray-600 data-[state=open]:bg-transparent dark:hover:bg-transparent dark:hover:text-gray-300"
										/>
									}
								>
									<DropdownMenuGroup>
										<DropdownMenuItem
											onSelect={() => onEdit(view)}
											Icon={IconPenFill18}
										>
											Edit
										</DropdownMenuItem>
										<DropdownMenuItem
											onSelect={() => onCopyLink(view)}
											Icon={IconCopyFill18}
										>
											Copy link
										</DropdownMenuItem>
										<DropdownMenuItem
											onSelect={() => onDelete(view.id)}
											Icon={IconTrashFill18}
											// className="text-red-500 focus:text-red-500"
										>
											Delete
										</DropdownMenuItem>
									</DropdownMenuGroup>
								</DropdownMenu>
							</div>
						</div>
						<span className="truncate">{view.name}</span>
					</div>
					{count > 0 && (
						<Badge variant="transparent" size="xs" className={INBOX_NAV_COUNT}>
							{label}
						</Badge>
					)}
				</div>
			</ContextMenuPrimitive.Trigger>
			<ContextMenuPrimitive.Portal>
				<ContextMenuPrimitive.Content className="dark:ring-white-10 z-50 min-w-[8rem] overflow-hidden rounded-lg bg-white p-1 text-primary shadow-lg ring-1 ring-black/10 !duration-150 ease-out-cubic data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 dark:bg-gray-850">
					<ContextMenuPrimitive.Item
						className={contextMenuItemClass}
						onSelect={() => onEdit(view)}
					>
						<IconPenFill18 className="mr-2 h-4 w-4 text-secondary" />
						Edit
					</ContextMenuPrimitive.Item>
					<ContextMenuPrimitive.Item
						className={contextMenuItemClass}
						onSelect={() => onCopyLink(view)}
					>
						<IconCopyFill18 className="mr-2 h-4 w-4 text-secondary" />
						Copy link
					</ContextMenuPrimitive.Item>
					<ContextMenuPrimitive.Item
						className={cn(
							contextMenuItemClass,
							"text-red-500 focus:text-red-500",
						)}
						onSelect={() => onDelete(view.id)}
					>
						<IconTrashFill18 className="mr-2 h-4 w-4" />
						Delete
					</ContextMenuPrimitive.Item>
				</ContextMenuPrimitive.Content>
			</ContextMenuPrimitive.Portal>
		</ContextMenuPrimitive.Root>
	)
}

export const CustomViewsList = memo(function CustomViewsList() {
	const zero = useZero()
	const navigate = useNavigate()
	// The combined sidebar keeps this list on every page, so a view only lights up on the inbox.
	const onThreads = useLocation().pathname.startsWith("/threads")
	const {
		activeCustomViewId,
		setActiveCustomViewId,
		setInboxState,
		pendingEditCustomViewId,
		setPendingEditCustomViewId,
	} = useThreadState()
	const currentFilters = useThreadsFilterState()

	const [customViews] = useQuery(queries.customViews())
	// Small, already-synced query (shared across the inbox), no load-time cost.
	const [teamMembers] = useQuery(queries.teamMembers())
	const usersById = useMemo(() => {
		const map = new Map<string, User>()
		for (const member of teamMembers ?? []) {
			if (member.user) map.set(member.user.id, member.user)
		}
		return map
	}, [teamMembers])
	const [editingId, setEditingId] = useState<string | null>(null)
	const editingView = customViews.find((view) => view.id === editingId)
	const savedTabFilters = useRef<Record<string, unknown> | null>(null)

	const isOpen = useIsInboxSectionOpen(INBOX_SECTIONS.views)
	const toggleSection = useInboxNavStore((s) => s.toggleSection)
	const openSection = useInboxNavStore((s) => s.openSection)
	// The mobile bar hides section headers, so a section collapsed on desktop
	// would strand its rows with no way to reopen them.
	const isMobile = useMedia("(max-width: 639px)", false)

	useEffect(() => {
		if (!pendingEditCustomViewId) return
		setEditingId(pendingEditCustomViewId)
		setPendingEditCustomViewId(null)
		openSection(INBOX_SECTIONS.views)
	}, [pendingEditCustomViewId, setPendingEditCustomViewId, openSection])

	const handleSelect = useCallback(
		(view: CustomView) => {
			setActiveCustomViewId(view.id)
			setInboxState("custom-view")
			void navigate({ to: "/threads", replace: onThreads })
		},
		[setActiveCustomViewId, setInboxState, navigate, onThreads],
	)

	const handleCreate = useCallback(() => {
		const id = generateId()
		const slug = generateId().replace(/-/g, "").slice(0, 12)
		const savedFilters = {
			negations: currentFilters.negations,
			companyIds: currentFilters.companyIds,
			personIds: currentFilters.personIds,
			issueIds: currentFilters.issueIds,
			tagIds: currentFilters.tagIds,
			projectIds: currentFilters.projectIds,
			assigneeIds: currentFilters.assigneeIds,
			origins: currentFilters.origins,
			portalInstanceIds: currentFilters.portalInstanceIds,
			slackWorkspaceIds: currentFilters.slackWorkspaceIds,
			slackChannelIds: currentFilters.slackChannelIds,
			emailAddressIds: currentFilters.emailAddressIds,
			painLevels: currentFilters.painLevels,
			creatorIds: currentFilters.creatorIds,
			statuses: currentFilters.statuses,
			replyStates: currentFilters.replyStates,
			companyTiers: currentFilters.companyTiers,
			companyStatuses: currentFilters.companyStatuses,
			companyOwnerIds: currentFilters.companyOwnerIds,
			issueStatuses: currentFilters.issueStatuses,
			companyRevenue: currentFilters.companyRevenue,
			companySize: currentFilters.companySize,
		}
		zero.mutate(
			mutators.customViews.create({
				id,
				name: "New view",
				slug,
				icon: null,
				filters: savedFilters,
				sortOrder: customViews.length,
			}),
		)
		setEditingId(id)
		openSection(INBOX_SECTIONS.views)
	}, [zero, currentFilters, customViews.length, openSection])

	const handleEdit = useCallback(
		(view: CustomView) => {
			// Save the current tab filter state so we can restore it after editing
			savedTabFilters.current = {
				negations: currentFilters.negations,
				companyIds: currentFilters.companyIds,
				personIds: currentFilters.personIds,
				issueIds: currentFilters.issueIds,
				tagIds: currentFilters.tagIds,
				projectIds: currentFilters.projectIds,
				assigneeIds: currentFilters.assigneeIds,
				origins: currentFilters.origins,
				portalInstanceIds: currentFilters.portalInstanceIds,
				slackWorkspaceIds: currentFilters.slackWorkspaceIds,
				slackChannelIds: currentFilters.slackChannelIds,
				emailAddressIds: currentFilters.emailAddressIds,
				painLevels: currentFilters.painLevels,
				creatorIds: currentFilters.creatorIds,
				statuses: currentFilters.statuses,
				replyStates: currentFilters.replyStates,
				companyTiers: currentFilters.companyTiers,
				companyStatuses: currentFilters.companyStatuses,
				companyOwnerIds: currentFilters.companyOwnerIds,
				issueStatuses: currentFilters.issueStatuses,
				companyRevenue: currentFilters.companyRevenue,
				companySize: currentFilters.companySize,
			}
			currentFilters.loadFilters(view.filters)
			handleSelect(view)
			setEditingId(view.id)
		},
		[currentFilters, handleSelect],
	)

	const handleCommitEdit = useCallback(
		(id: string, { name, icon }: { name: string; icon: string }) => {
			zero.mutate(
				mutators.customViews.update({
					id,
					name,
					icon,
					filters: {
						negations: currentFilters.negations,
						companyIds: currentFilters.companyIds,
						personIds: currentFilters.personIds,
						issueIds: currentFilters.issueIds,
						tagIds: currentFilters.tagIds,
						projectIds: currentFilters.projectIds,
						assigneeIds: currentFilters.assigneeIds,
						origins: currentFilters.origins,
						portalInstanceIds: currentFilters.portalInstanceIds,
						slackWorkspaceIds: currentFilters.slackWorkspaceIds,
						slackChannelIds: currentFilters.slackChannelIds,
						emailAddressIds: currentFilters.emailAddressIds,
						painLevels: currentFilters.painLevels,
						creatorIds: currentFilters.creatorIds,
						statuses: currentFilters.statuses,
						replyStates: currentFilters.replyStates,
						companyTiers: currentFilters.companyTiers,
						companyStatuses: currentFilters.companyStatuses,
						companyOwnerIds: currentFilters.companyOwnerIds,
						issueStatuses: currentFilters.issueStatuses,
						companyRevenue: currentFilters.companyRevenue,
						companySize: currentFilters.companySize,
					},
				}),
			)
			// Clear filter chips after saving — the filters now live on the view
			// itself, so leaving them displayed at the top of the inbox is confusing.
			currentFilters.reset()
			savedTabFilters.current = null
			setEditingId(null)
		},
		[zero, currentFilters],
	)

	const handleCancelEdit = useCallback(() => {
		// Restore tab filters on cancel
		if (savedTabFilters.current) {
			currentFilters.loadFilters(
				savedTabFilters.current as Parameters<
					typeof currentFilters.loadFilters
				>[0],
			)
			savedTabFilters.current = null
		}
		setEditingId(null)
	}, [currentFilters])

	const handleDelete = useCallback(
		(id: string) => {
			zero.mutate(mutators.customViews.delete({ id }))
			if (activeCustomViewId === id) {
				setActiveCustomViewId(null)
			}
		},
		[zero, activeCustomViewId, setActiveCustomViewId],
	)

	const handleCopyLink = useCallback(async (view: CustomView) => {
		const url = `${window.location.origin}/threads?view=${view.slug}`
		try {
			await navigator.clipboard.writeText(url)
			toast.success({ title: "Link copied" })
		} catch {
			toast.error({ title: "Could not copy link" })
		}
	}, [])

	return (
		<div
			className={cn(
				"group/inbox-section mt-4 max-sm:contents",
				INBOX_NAV_GROUP,
			)}
		>
			<NavSectionHeader
				label="Views"
				hoverGroup="inbox-section"
				isOpen={isOpen}
				onToggle={() => toggleSection(INBOX_SECTIONS.views)}
				className={cn(INBOX_NAV_SECTION_HEADER, "max-sm:hidden")}
				action={
					<IconButton
						Icon={PlusIcon}
						variant="ghost"
						aria-label="Add view"
						onClick={handleCreate}
						iconStrokeWidth={1}
						className="group/add mr-[7px] h-4 w-4 shrink-0 transform-gpu text-tertiary hover:bg-transparent hover:text-secondary"
						iconClassName="h-3.5 w-3.5 group-hover/add:text-secondary"
					/>
				}
			/>
			<NavSectionBody
				isOpen={isOpen}
				inert={!isOpen && !isMobile}
				className="max-sm:contents"
				innerClassName="max-sm:contents"
			>
				<div className={cn(INBOX_NAV_GROUP, "pb-0.5 max-sm:contents")}>
					{customViews.length === 0 ? (
						<div className="max-sm:hidden">
							<SidebarAddButton
								label="Create view"
								visible
								hoverGroup="views"
								onClick={handleCreate}
								className={INBOX_NAV_ADD_BUTTON}
							/>
						</div>
					) : (
						customViews.map((view, index) => {
							const assigneeId = soleAssigneeId(view)
							return (
								<CustomViewItem
									key={view.id}
									view={view}
									countCap={customViewCountCap(index)}
									isActive={onThreads && activeCustomViewId === view.id}
									assignee={assigneeId ? usersById.get(assigneeId) : undefined}
									onSelect={() => handleSelect(view)}
									onEdit={handleEdit}
									onDelete={handleDelete}
									onCopyLink={handleCopyLink}
								/>
							)
						})
					)}
				</div>
			</NavSectionBody>
			{editingView && (
				<EditViewDialog
					key={editingView.id}
					name={editingView.name}
					icon={editingView.icon ?? DEFAULT_ICON}
					onSave={(values) => handleCommitEdit(editingView.id, values)}
					onCancel={handleCancelEdit}
				/>
			)}
		</div>
	)
})
